import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { FormBuilder } from '@ginkgo-bioworks/react-json-schema-form-builder';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import Loader from '@Components/Loader';
import { CUSTOM_FORMS_PATH } from '@Components/CustomForms';
import {
  CUSTOM_FORMS_QUERY_KEY,
  customFormQueryKey,
  useCustomForm,
  useCustomForms,
} from '@Hooks/useCustomForms';
import serviceMethods from '../../service';
import { BOOTSTRAP_SCOPE_CLASS, scopedBootstrapCss } from './scopedBootstrap';

const EMPTY_SCHEMA = '{}';

// The builder takes and emits schemas as JSON strings; stored schemas are Json
// columns that can also arrive already stringified.
const toSchemaString = (value) =>
  typeof value === 'string' ? value : JSON.stringify(value ?? {});

const schemaTitle = (formSchema) => {
  try {
    return JSON.parse(formSchema).title?.trim() ?? '';
  } catch {
    return '';
  }
};

const CustomFormEditor = ({ id, customForm, responseCount }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formSchema, setFormSchema] = useState(
    customForm ? toSchemaString(customForm.formSchemaJson) : EMPTY_SCHEMA,
  );
  const [uiSchema, setUiSchema] = useState(
    customForm ? toSchemaString(customForm.uiSchemaJson) : EMPTY_SCHEMA,
  );
  const [formData, setFormData] = useState({});

  const isDeleted = !!customForm?.deletedAt;
  const title = schemaTitle(formSchema);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        title,
        formSchemaJson: JSON.parse(formSchema),
        uiSchemaJson: JSON.parse(uiSchema),
      };
      if (id) return serviceMethods.updateCustomForm(id, body);

      const res = await serviceMethods.createCustomForm(body);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Error creating custom form');
      return data;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: CUSTOM_FORMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: customFormQueryKey(saved.id) });
      toastSuccess(id ? 'Custom form saved.' : 'Custom form created.');
      if (!id) navigate(`${CUSTOM_FORMS_PATH}/${saved.id}`, { replace: true });
    },
    onError: (e) =>
      toastError(e instanceof Error ? e.message : 'Failed to save custom form'),
  });

  return (
    <div className="flex flex-col gap-4">
      <style>{scopedBootstrapCss}</style>

      <div className="flex items-center justify-between">
        <Heading>{id ? title || 'Untitled form' : 'New custom form'}</Heading>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!title || isDeleted || saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : id ? 'Save changes' : 'Create form'}
        </Button>
      </div>

      {isDeleted && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          This form was deleted and can't be edited.
        </p>
      )}
      {!title && !isDeleted && (
        <p className="text-sm text-zinc-500">Give the form a title in the builder to save it.</p>
      )}
      {responseCount > 0 && !isDeleted && (
        <p className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
          This form has {responseCount} response{responseCount === 1 ? '' : 's'}. Renaming or
          removing a field leaves earlier answers under the old field name.
        </p>
      )}

      <div className="flex w-full gap-4">
        <div className={`w-[50%] ${BOOTSTRAP_SCOPE_CLASS}`}>
          <FormBuilder
            className="w-full"
            schema={formSchema}
            uischema={uiSchema}
            onChange={(newFormSchema, newUiSchema) => {
              setFormSchema(newFormSchema);
              setUiSchema(newUiSchema);
            }}
          />
        </div>

        <div className="w-[50%]">
          <h2 className="font-bold">Form Preview</h2>
          <Form
            className={`${BOOTSTRAP_SCOPE_CLASS} rounded border px-4 py-3`}
            schema={JSON.parse(formSchema)}
            uiSchema={JSON.parse(uiSchema)}
            validator={validator}
            formData={formData}
            onChange={({ formData }) => setFormData(formData)}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Create (`/custom-form-builder/new`) or edit (`/custom-form-builder/:id`) a
 * custom form. Creating saves once and moves to the new form's edit URL, which
 * reloads it from the API; later saves update the same id.
 */
const CustomFormBuilder = () => {
  const { id } = useParams();
  const { data: customForm, isLoading, isError } = useCustomForm(id);
  const { data: forms } = useCustomForms();
  const responseCount = forms?.find((form) => form.id === id)?.numberOfResponses ?? 0;

  return (
    <div>
      <Breadcrumbs
        navList={[
          { name: 'Custom Forms', href: CUSTOM_FORMS_PATH },
          {
            name: id ? customForm?.title ?? 'Edit form' : 'New form',
            href: id ? `${CUSTOM_FORMS_PATH}/${id}` : `${CUSTOM_FORMS_PATH}/new`,
          },
        ]}
      />
      <div className="mt-8">
        {id && isLoading ? (
          <Loader />
        ) : id && isError ? (
          <p className="text-sm text-red-600">This custom form could not be loaded.</p>
        ) : (
          <CustomFormEditor
            key={id ?? 'new'}
            id={id}
            customForm={customForm}
            responseCount={responseCount}
          />
        )}
      </div>
    </div>
  );
};

export default CustomFormBuilder;
